import { AfterViewInit, Component, DestroyRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { ActivatedRoute, Router, ParamMap, RouterLink } from '@angular/router';
import { debounceTime, finalize } from 'rxjs/operators';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { FilterConfig, ListFilter, Webhook } from '@parashift/shared/models';
import { WebhookService, JsonApiQueryData, ListFilterService, Logger, ErrorResponse } from '@parashift/shared/services';
import { FilterColumnComponent, FilterSortingComponent, FilterTagsComponent, LeftColumnComponent, ListDataWithTooltipComponent, ListPaginationComponent } from '@parashift/shared/components';
import { HeaderActionStore } from '@parashift/shared/stores';

@Component({
  selector: 'webhooks-list',
  templateUrl: './webhooks-list.component.html',
  styleUrls: ['./webhooks-list.component.scss'],
  imports: [
    FaIconComponent,
    FilterColumnComponent,
    FilterSortingComponent,
    FilterTagsComponent,
    LeftColumnComponent,
    ListDataWithTooltipComponent,
    ListPaginationComponent,
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    TooltipModule
]
})
export class WebhooksListComponent implements AfterViewInit, OnInit {
  private webhookService = inject(WebhookService);
  private listFilterService = inject(ListFilterService);
  private headerActionStore = inject(HeaderActionStore);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private logger = inject(Logger);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @ViewChild(FilterColumnComponent) filterComponent: FilterColumnComponent;
  @Input() filterColumnConfig: FilterConfig;

  webhooks: JsonApiQueryData<Webhook>;
  listFilter: ListFilter;
  loading = true;

  private tenant_id: number;

  constructor() {
    const destroyRef = this.destroyRef;
    destroyRef.onDestroy(() => this.headerActionStore.destroy());
  }

  ngOnInit() {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.tenant_id = params.get('tenant_id') ? Number(params.get('tenant_id')) : undefined;
      this.initListFilter();
      this.getWebhooks();
      this.headerActionStore.addActions({
        newButton: { dispatch: () => this.goToAddNewWebhook() }
      });

    });
  }

  initListFilter() {
    this.listFilter = this.listFilterService.getStoredFilters(this.filterColumnConfig.meta.identifier, { stats: { total: ['count'] } }, true);
  }

  ngAfterViewInit() {
    this.filterComponent.$filterParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(400)
      )
      .subscribe(listFilter => {
        this.listFilter = listFilter as ListFilter;
        this.getWebhooks();
      });
  }

  getWebhooks() {
    this.loading = true;
    this.webhookService.findAll(this.listFilter, this.tenant_id)
    .pipe(finalize(() => this.loading = false))
    .subscribe({
      next: (webhooks: JsonApiQueryData<Webhook>) => {
        this.webhooks = webhooks;
      },
      error: (error: ErrorResponse) => {
        this.logger.log('webhooks-list-component', 'WEBHOOKS Error', error);
      }
    });
  }

  goToDetailView(id: string, event: MouseEvent) {
    if (!this.shouldOpenInNewTab(event)) {
      this.router.navigate([id, 'detail'], { relativeTo: this.route });
    }
  }

  editWebhook(id: string, event: MouseEvent) {
    if (!this.shouldOpenInNewTab(event)) {
      this.router.navigate([id, 'edit'], { relativeTo: this.route });
    }
  }

  goToAddNewWebhook() {
    this.router.navigate(['new', 'edit'], { relativeTo: this.route });
  }

  private shouldOpenInNewTab(event: MouseEvent) {
    return event.metaKey === true || event.ctrlKey === true; // metaKey = Mac, ctrlKey = Windows
  }
}
